import type { Relationship } from "@pax/api-contract/common";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  HapticSurface,
  Skeleton,
} from "@pax/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Clock, UserRoundPlus } from "lucide-react";
import { requestErrorMessage } from "@/lib/api";
import { changeFriendship, type FriendChange } from "../queries";

/** Where you stand with someone, as the one control that moves it on. */
export function FriendButton({
  profileId,
  relationship,
}: {
  profileId: string;
  relationship: Relationship | undefined;
}) {
  const queryClient = useQueryClient();
  const change = useMutation({
    mutationFn: (next: FriendChange) => changeFriendship(queryClient, profileId, next),
  });

  if (relationship === undefined) return <Skeleton className="h-9 w-28 rounded-lg" />;
  if (relationship === "self") return null;

  return (
    <div className="flex flex-col items-center gap-1">
      {relationship === "none" ? (
        <Button size="lg" disabled={change.isPending} onClick={() => change.mutate("request")}>
          <UserRoundPlus aria-hidden />
          Add friend
          <HapticSurface />
        </Button>
      ) : relationship === "incoming" ? (
        <Button size="lg" disabled={change.isPending} onClick={() => change.mutate("accept")}>
          <Check aria-hidden />
          Accept request
          <HapticSurface />
        </Button>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={<Button size="lg" variant="outline" disabled={change.isPending} />}
          >
            {relationship === "friends" ? <Check aria-hidden /> : <Clock aria-hidden />}
            {relationship === "friends" ? "Friends" : "Requested"}
            <ChevronDown aria-hidden className="text-muted-foreground" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {relationship === "friends" ? (
              <DropdownMenuItem variant="destructive" onClick={() => change.mutate("remove")}>
                Remove friend
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => change.mutate("cancel")}>
                Cancel request
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {change.error ? (
        <p role="alert" className="text-xs text-destructive">
          {requestErrorMessage(change.error)}
        </p>
      ) : null}
    </div>
  );
}
